import moment from 'moment-timezone'
import { formatCurrency } from '@smarttransit/common'
import { addParamsToLocation } from '@smarttransit/common-client'
import { addAlert } from '@/utilities/helpers'
import { findSourceRecords, findAggregateRecords } from '@/services/reports-service'
import { findTransportationProfiles } from '@/services/transportation-profiles-service'

const AD_HOC_NOTES_TOOLTIP_TEXT_LIMIT = 1000

function toNumber (value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function toRoundedMoney (value) {
  return Number(toNumber(value).toFixed(2))
}

function toRoundedCount (value) {
  return Math.round(toNumber(value))
}

function escapeHtml (str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export default {
  name: 'ticket-revenue-reports',
  props: {
    vehicleIds: {
      type: Array,
      default: function () {
        return []
      }
    },
    dateFrom: {
      type: String,
      default: null
    },
    dateTo: {
      type: String,
      default: null
    },
    isTransportationOwnerUser: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loadingCharts: false,
      showWeekendRevenue: false,
      selectedChartView: 'gross',
      reportCurrencySymbol: '',
      dayChart: {
        title: 'Revenue per Day',
        categories: [],
        buckets: []
      },
      weekChart: {
        title: 'Revenue per Week',
        categories: [],
        buckets: []
      },
      monthChart: {
        title: 'Revenue per Month',
        categories: [],
        buckets: []
      },
      quarterChart: {
        title: 'Revenue per Quarter',
        categories: [],
        buckets: []
      },
      yearChart: {
        title: 'Revenue per Year',
        categories: [],
        buckets: []
      },
      chartVisibility: {
        day: true,
        week: true,
        month: true,
        quarter: true,
        year: true
      },
      vehicleNameById: {},
      vehicleAgencyById: {},
      requestSequence: 0,
      lastQueryParams: null
    }
  },
  created () {
    this.loadFiltersFromQuery()
  },
  computed: {
    normalizedVehicleIds () {
      const ids = Array.isArray(this.vehicleIds) ? this.vehicleIds : []
      const uniqueMap = {}
      const normalized = []

      for (let i = 0; i < ids.length; i += 1) {
        const rawId = ids[i]
        if (rawId === null || rawId === undefined || String(rawId).trim() === '') {
          continue
        }

        const idKey = String(rawId)
        if (uniqueMap[idKey]) {
          continue
        }

        uniqueMap[idKey] = true
        normalized.push(idKey)
      }

      return normalized
    },
    hasCustomDateRange () {
      return !!(this.dateFrom && this.dateTo)
    },
    computedDateRangeDays () {
      if (!this.hasCustomDateRange) {
        return null
      }
      const start = moment.utc(this.dateFrom, 'YYYY-MM-DD', true)
      const end = moment.utc(this.dateTo, 'YYYY-MM-DD', true)
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
        return null
      }
      return end.diff(start, 'days') + 1
    },
    computedCanShowWeek () {
      if (!this.hasCustomDateRange) {
        return true
      }
      return Number(this.computedDateRangeDays || 0) >= 7
    },
    computedCanShowMonth () {
      if (!this.hasCustomDateRange) {
        return true
      }
      return Number(this.computedDateRangeDays || 0) >= 28
    },
    computedCanShowQuarter () {
      if (!this.hasCustomDateRange) {
        return true
      }
      return Number(this.computedDateRangeDays || 0) >= 90
    },
    computedCanShowYear () {
      if (!this.hasCustomDateRange) {
        return true
      }
      return Number(this.computedDateRangeDays || 0) >= 365
    },
    chartViewOptions () {
      const options = []

      options.push({
        text: this.showWeekendRevenue ? 'Weekend Revenue' : 'Gross Breakdown',
        value: 'gross'
      })

      if (!this.showWeekendRevenue) {
        options.push({ text: 'Revenue Sources', value: 'sources' })
        options.push({ text: 'Fee Details', value: 'fees' })
      }

      options.push({ text: 'Ticket Count', value: 'tickets' })

      if (!this.isTransportationOwnerUser) {
        options.push({ text: 'Refunds', value: 'refunds' })
      }

      return options
    },
    chartViewKey () {
      return this.chartViewOptions.map(function (item) { return item.value }).join('|')
    },
    tooltipTotalLabel () {
      switch (this.selectedChartView) {
        case 'gross': return 'Gross Amount'
        case 'sources': return 'Total Revenue'
        case 'fees': return 'Total Fees'
        case 'refunds': return 'Total Refunds'
        default: return null
      }
    },
    fetchKey () {
      return JSON.stringify({
        vehicleIds: this.normalizedVehicleIds,
        dateFrom: this.dateFrom || null,
        dateTo: this.dateTo || null
      })
    },
    dayChartOptions () {
      return this.chartOptions(this.dayChart)
    },
    weekChartOptions () {
      return this.chartOptions(this.weekChart)
    },
    monthChartOptions () {
      return this.chartOptions(this.monthChart)
    },
    quarterChartOptions () {
      return this.chartOptions(this.quarterChart)
    },
    yearChartOptions () {
      return this.chartOptions(this.yearChart)
    },
    dayChartRender () {
      return this.renderChartState(this.dayChart)
    },
    weekChartRender () {
      return this.renderChartState(this.weekChart)
    },
    monthChartRender () {
      return this.renderChartState(this.monthChart)
    },
    quarterChartRender () {
      return this.renderChartState(this.quarterChart)
    },
    yearChartRender () {
      return this.renderChartState(this.yearChart)
    },
    dayChartTooltipExtras () {
      const buckets = this.dayChart && Array.isArray(this.dayChart.buckets) ? this.dayChart.buckets : []
      const self = this

      return buckets.map(function (bucket) {
        const paidOut = !!(bucket && bucket.paidOut === true)
        const paidOutDatesRaw = bucket && Array.isArray(bucket.paidOutDates) ? bucket.paidOutDates : []
        const paidOutDatesMap = {}
        const paidOutDateLabels = []

        for (let p = 0; p < paidOutDatesRaw.length; p += 1) {
          const paidOutDateValue = String(paidOutDatesRaw[p] || '').trim()
          if (!paidOutDateValue) {
            continue
          }

          const paidOutDateMoment = moment.utc(paidOutDateValue)
          const paidOutDateLabel = paidOutDateMoment.isValid()
            ? paidOutDateMoment.format('YYYY-MM-DD')
            : paidOutDateValue

          if (!paidOutDatesMap[paidOutDateLabel]) {
            paidOutDatesMap[paidOutDateLabel] = true
            paidOutDateLabels.push(paidOutDateLabel)
          }
        }

        paidOutDateLabels.sort()
        const payoutHeaderLabel = paidOut ? 'Paid out' : 'Pending payout'
        const payoutLines = []
        if (paidOut && paidOutDateLabels.length) {
          payoutLines.push('<div>Payout date: ' + escapeHtml(paidOutDateLabels.join(', ')) + '</div>')
        }

        const payoutSectionHtml = '<div style="font-weight:700;margin-top:3px;">' + payoutHeaderLabel + '</div>' + payoutLines.join('')

        const notesByVehicle = bucket && bucket.adHocNotesByVehicle && typeof bucket.adHocNotesByVehicle === 'object'
          ? bucket.adHocNotesByVehicle
          : {}
        const vehicleIds = Object.keys(notesByVehicle)

        if (!vehicleIds.length) {
          return payoutSectionHtml
        }

        const sections = []
        const showVehicleHeaders = vehicleIds.length > 1
        let notesCharactersUsed = 0
        let notesTruncated = false

        for (let i = 0; i < vehicleIds.length; i += 1) {
          const vehicleId = vehicleIds[i]
          const noteEntries = Array.isArray(notesByVehicle[vehicleId]) ? notesByVehicle[vehicleId] : []
          const sanitizedEntries = noteEntries
            .map(function (entry) { return String(entry || '').trim() })
            .filter(function (entry) { return !!entry })

          if (!sanitizedEntries.length) {
            continue
          }

          const escapedNoteLines = []
          for (let n = 0; n < sanitizedEntries.length; n += 1) {
            const remainingCharacters = AD_HOC_NOTES_TOOLTIP_TEXT_LIMIT - notesCharactersUsed
            if (remainingCharacters <= 0) {
              notesTruncated = true
              break
            }

            let noteText = sanitizedEntries[n]
            if (noteText.length > remainingCharacters) {
              noteText = noteText.slice(0, remainingCharacters)
              notesTruncated = true
            }

            notesCharactersUsed += noteText.length
            escapedNoteLines.push(escapeHtml(noteText))

            if (notesTruncated) {
              break
            }
          }

          if (!escapedNoteLines.length) {
            continue
          }
          if (showVehicleHeaders) {
            sections.push('<div style="font-weight:600;margin-top:3px;">' + escapeHtml(self.vehicleName(vehicleId)) + '</div><div>' + escapedNoteLines.join('<br>') + '</div>')
          } else {
            sections.push('<div>' + escapedNoteLines.join('<br>') + '</div>')
          }

          if (notesTruncated) {
            break
          }
        }

        if (!sections.length) {
          return payoutSectionHtml
        }

        const truncationMessage = notesTruncated
          ? '<div style="font-style:italic;margin-top:3px;">Notes truncated after ' + AD_HOC_NOTES_TOOLTIP_TEXT_LIMIT + ' characters</div>'
          : ''

        return payoutSectionHtml + '<div style="font-weight:700;margin-top:3px;">Notes</div>' + sections.join('') + truncationMessage
      })
    }
  },
  watch: {
    fetchKey: {
      immediate: true,
      handler () {
        this.fetchReportData()
      }
    },
    chartViewKey: {
      immediate: true,
      handler () {
        this.ensureSelectedChartView()
      }
    },
    showWeekendRevenue () {
      this.syncFiltersToQuery()
    },
    selectedChartView () {
      this.syncFiltersToQuery()
    }
  },
  methods: {
    loadFiltersFromQuery () {
      const query = this.$route && this.$route.query ? this.$route.query : {}

      if (!this.isTransportationOwnerUser && (query.weekendRevenue === 'true' || query.weekendRevenue === '1')) {
        this.showWeekendRevenue = true
      }

      if (query.chartView) {
        this.selectedChartView = query.chartView
      }
    },
    syncFiltersToQuery () {
      this.lastQueryParams = addParamsToLocation({
        weekendRevenue: this.showWeekendRevenue ? '1' : null,
        chartView: this.selectedChartView || null
      }, this.$route, this.lastQueryParams)
    },
    emptyChartState (title) {
      return {
        title: title,
        categories: [],
        buckets: [],
        reportRows: []
      }
    },
    resetChartsToEmptyState () {
      this.dayChart = this.emptyChartState('Revenue per Day')
      this.weekChart = this.emptyChartState('Revenue per Week')
      this.monthChart = this.emptyChartState('Revenue per Month')
      this.quarterChart = this.emptyChartState('Revenue per Quarter')
      this.yearChart = this.emptyChartState('Revenue per Year')
    },
    ensureSelectedChartView () {
      const allowedViews = this.chartViewOptions.map(function (item) { return item.value })
      if (allowedViews.includes(this.selectedChartView)) {
        return
      }
      this.selectedChartView = allowedViews.length ? allowedViews[0] : 'gross'
    },
    async ensureVehicleNames (vehicleIds) {
      const missingIds = vehicleIds.filter((id) => {
        if (!id) {
          return false
        }
        return !this.vehicleNameById[id] || !this.vehicleAgencyById[id]
      })
      if (!missingIds.length) {
        return
      }

      const profiles = await findTransportationProfiles({
        where: { id: { inq: missingIds } },
        fields: { id: true, licensePlateNumber: true, agencyId: true },
        limit: 10000
      })

      for (let i = 0; i < profiles.length; i += 1) {
        const profile = profiles[i]
        if (profile && profile.id) {
          this.vehicleNameById[profile.id] = profile.licensePlateNumber || profile.id
          if (profile.agencyId) {
            this.vehicleAgencyById[profile.id] = String(profile.agencyId)
          }
        }
      }

      for (let j = 0; j < missingIds.length; j += 1) {
        const id = missingIds[j]
        if (!this.vehicleNameById[id]) {
          this.vehicleNameById[id] = 'Vehicle ' + String(id).slice(0, 8)
        }
      }
    },
    vehicleName (vehicleId) {
      return this.vehicleNameById[vehicleId] || ('Vehicle ' + String(vehicleId).slice(0, 8))
    },
    resolveCurrencySymbolForVehicleIds (vehicleIds, countryCurrencies) {
      const agencies = this.$store && this.$store.getters ? this.$store.getters.getAgencies : []

      if (!Array.isArray(agencies) || !countryCurrencies) {
        return ''
      }

      const agencyById = {}
      for (let i = 0; i < agencies.length; i += 1) {
        const agency = agencies[i]
        if (agency && agency.value) {
          agencyById[String(agency.value)] = agency
        }
      }

      const selectedVehicleIds = Array.isArray(vehicleIds) ? vehicleIds : []
      for (let i = 0; i < selectedVehicleIds.length; i += 1) {
        const vehicleId = selectedVehicleIds[i]
        const agencyId = this.vehicleAgencyById[vehicleId]

        if (!agencyId) {
          continue
        }

        const agency = agencyById[agencyId]
        const countryId = agency && agency.metadata ? agency.metadata.countryId : null

        if (!countryId) {
          continue
        }

        const countryCurrency = countryCurrencies[countryId]

        if (countryCurrency && countryCurrency.currencySymbol) {
          return String(countryCurrency.currencySymbol)
        }
      }

      return ''
    },
    formatAmountLabel (value) {
      return formatCurrency(value, this.reportCurrencySymbol || '')
    },
    formatAmountAxisLabel (value) {
      return formatCurrency(value, '')
    },
    async fetchReportData () {
      const requestId = this.requestSequence + 1
      this.requestSequence = requestId

      const vehicleIds = this.normalizedVehicleIds
      if (!vehicleIds.length) {
        this.resetChartsToEmptyState()
        return
      }

      this.loadingCharts = true
      try {
        await this.ensureVehicleNames(vehicleIds)
        const countryCurrencies = await this.$store.getters.getCountryCurrencies
        if (requestId !== this.requestSequence) {
          return
        }
        void countryCurrencies
        this.reportCurrencySymbol = this.resolveCurrencySymbolForVehicleIds(vehicleIds, countryCurrencies)
        if (requestId !== this.requestSequence) {
          return
        }

        this.chartVisibility = {
          day: true,
          week: this.computedCanShowWeek,
          month: this.computedCanShowMonth,
          quarter: this.computedCanShowQuarter,
          year: this.computedCanShowYear
        }

        const jobs = []
        jobs.push(this.buildChartForFrequency('day', vehicleIds))
        jobs.push(this.chartVisibility.week ? this.buildChartForFrequency('week', vehicleIds) : Promise.resolve(this.emptyChartState('Revenue per Week')))
        jobs.push(this.chartVisibility.month ? this.buildChartForFrequency('month', vehicleIds) : Promise.resolve(this.emptyChartState('Revenue per Month')))
        jobs.push(this.chartVisibility.quarter ? this.buildChartForFrequency('quarter', vehicleIds) : Promise.resolve(this.emptyChartState('Revenue per Quarter')))
        jobs.push(this.chartVisibility.year ? this.buildChartForFrequency('year', vehicleIds) : Promise.resolve(this.emptyChartState('Revenue per Year')))

        const [dayChart, weekChart, monthChart, quarterChart, yearChart] = await Promise.all(jobs)
        if (requestId !== this.requestSequence) {
          return
        }

        this.dayChart = dayChart
        this.weekChart = weekChart
        this.monthChart = monthChart
        this.quarterChart = quarterChart
        this.yearChart = yearChart
      } catch (err) {
        if (requestId === this.requestSequence) {
          addAlert({
            message: err,
            type: 'error'
          })
        }
      } finally {
        if (requestId === this.requestSequence) {
          this.loadingCharts = false
        }
      }
    },
    resolveRangeForFrequency (frequency) {
      if (this.hasCustomDateRange) {
        const rangeStart = moment.utc(this.dateFrom, 'YYYY-MM-DD').startOf('day')
        const rangeEndExclusive = moment.utc(this.dateTo, 'YYYY-MM-DD').startOf('day').add(1, 'day')
        return { rangeStart: rangeStart, rangeEndExclusive: rangeEndExclusive }
      }

      if (frequency === 'day') {
        return {
          rangeStart: moment.utc().startOf('day').subtract(6, 'day'),
          rangeEndExclusive: moment.utc().startOf('day').add(1, 'day')
        }
      }

      if (frequency === 'week') {
        return {
          rangeStart: moment.utc().startOf('isoWeek').subtract(1, 'week'),
          rangeEndExclusive: moment.utc().startOf('isoWeek')
        }
      }

      if (frequency === 'month') {
        return {
          rangeStart: moment.utc().startOf('month').subtract(1, 'month'),
          rangeEndExclusive: moment.utc().startOf('month')
        }
      }

      if (frequency === 'quarter') {
        return {
          rangeStart: moment.utc().startOf('quarter').subtract(1, 'quarter'),
          rangeEndExclusive: moment.utc().startOf('quarter')
        }
      }

      return {
        rangeStart: moment.utc().startOf('year').subtract(1, 'year'),
        rangeEndExclusive: moment.utc().startOf('year')
      }
    },
    resolveApiPeriodBounds () {
      const defaultStart = moment.utc().startOf('day')
      const defaultEnd = moment.utc().hour(23).minute(59).second(0).millisecond(0)

      const start = this.dateFrom
        ? moment.utc(this.dateFrom, 'YYYY-MM-DD', true).startOf('day')
        : defaultStart.clone()
      const end = this.dateTo
        // Report records are stored with periodEnd at the next interval boundary
        // (e.g. yearly record ends on Jan 1 of the next year), so dateTo needs an exclusive-day ceiling.
        ? moment.utc(this.dateTo, 'YYYY-MM-DD', true).startOf('day').add(1, 'day')
        : defaultEnd.clone()

      const safeStart = start.isValid() ? start : defaultStart.clone()
      let safeEnd = end.isValid() ? end : defaultEnd.clone()

      if (safeEnd.isBefore(safeStart)) {
        safeEnd = safeStart.clone().add(1, 'day')
      }

      return {
        periodStart: safeStart.toISOString(),
        periodEnd: safeEnd.toISOString()
      }
    },
    emptyBucket () {
      return {
        totalAmount: 0,
        totalFeeDeduction: 0,
        totalTicketAmountBeforeFees: 0,
        adHocAmountBeforeFees: 0,
        smarttransytFee: 0,
        perDayDriverFee: 0,
        totalKwhCost: 0,
        totalRefundAmount: 0,
        weekendAmount: 0,
        weekendRefundAmount: 0,
        paidOut: false,
        paidOutDates: [],
        totalAmountByVehicle: {},
        totalFeeDeductionByVehicle: {},
        totalTicketAmountBeforeFeesByVehicle: {},
        adHocAmountBeforeFeesByVehicle: {},
        smarttransytFeeByVehicle: {},
        perDayDriverFeeByVehicle: {},
        totalKwhCostByVehicle: {},
        totalRefundAmountByVehicle: {},
        weekendAmountByVehicle: {},
        weekendRefundAmountByVehicle: {},
        ticketCountByVehicle: {},
        weekendTicketCountByVehicle: {},
        adHocNotesByVehicle: {}
      }
    },
    addBucketMetricByVehicle (bucket, metricKey, vehicleId, value) {
      if (!bucket || !metricKey || !vehicleId) {
        return
      }

      if (!bucket[metricKey]) {
        bucket[metricKey] = {}
      }

      if (!bucket[metricKey][vehicleId]) {
        bucket[metricKey][vehicleId] = 0
      }

      bucket[metricKey][vehicleId] += toNumber(value)
    },
    flattenDataValue (value, prefix, output) {
      const target = output || {}
      const keyPrefix = prefix || ''

      if (value === null || value === undefined) {
        target[keyPrefix] = ''
        return target
      }

      if (Array.isArray(value)) {
        target[keyPrefix] = value
          .map(function (item) {
            if (item === null || item === undefined) {
              return ''
            }
            return typeof item === 'object' ? JSON.stringify(item) : String(item)
          })
          .join(' | ')
        return target
      }

      if (typeof value === 'object') {
        const keys = Object.keys(value)
        for (let i = 0; i < keys.length; i += 1) {
          const childKey = keys[i]
          const childValue = value[childKey]
          const compoundKey = keyPrefix ? (keyPrefix + '.' + childKey) : childKey
          this.flattenDataValue(childValue, compoundKey, target)
        }
        return target
      }

      target[keyPrefix] = value
      return target
    },
    csvCell (value) {
      const normalizedValue = value === null || value === undefined ? '' : String(value)
      const escapedValue = normalizedValue.replace(/"/g, '""')
      return '"' + escapedValue + '"'
    },
    isCurrencyCsvDataKey (key) {
      if (!key) {
        return false
      }

      const normalizedKey = String(key).toLowerCase()
      return normalizedKey.includes('amount') ||
        normalizedKey.includes('fee') ||
        normalizedKey.includes('refund') ||
        normalizedKey.includes('revenue')
    },
    formatReportDataValueForCsv (key, value) {
      if (key === 'paidOut') {
        return value === true ? 'Yes' : 'No'
      }

      if (!this.isCurrencyCsvDataKey(key)) {
        return value
      }

      if (value === null || value === undefined || value === '') {
        return ''
      }

      const numericValue = Number(value)
      if (!Number.isFinite(numericValue)) {
        return value
      }

      return formatCurrency(value, '')
    },
    rowsToCsv (columns, rows) {
      const safeColumns = Array.isArray(columns) ? columns : []
      const safeRows = Array.isArray(rows) ? rows : []
      const lines = []

      lines.push(safeColumns.map((column) => this.getCsvHeaderLabel(column)).join(','))
      for (let i = 0; i < safeRows.length; i += 1) {
        const row = safeRows[i] || {}
        const cells = safeColumns.map((column) => this.csvCell(row[column]))
        lines.push(cells.join(','))
      }

      return lines.join('\n')
    },
    buildReportRows (records, frequency) {
      const safeRecords = Array.isArray(records) ? records : []
      const rows = []

      for (let i = 0; i < safeRecords.length; i += 1) {
        const record = safeRecords[i] || {}
        const vehicleId = record.categoryId ? String(record.categoryId) : ''
        const periodStart = record.periodStart || ''
        const bucketKey = periodStart ? this.bucketKeyForDate(frequency, periodStart) : ''
        const flattenedData = this.flattenDataValue(record.data || {}, '', {})
        const row = {
          frequency: frequency,
          periodStart: periodStart,
          bucketKey: bucketKey,
          bucketLabel: periodStart ? this.bucketLabelForDate(frequency, periodStart) : '',
          categoryId: vehicleId,
          vehicleName: vehicleId ? this.vehicleName(vehicleId) : ''
        }
        const dataKeys = Object.keys(flattenedData)
        for (let j = 0; j < dataKeys.length; j += 1) {
          const key = dataKeys[j]
          row['data.' + key] = this.formatReportDataValueForCsv(key, flattenedData[key])
        }
        rows.push(row)
      }

      return rows
    },
    shouldExcludeCsvColumn (columnKey) {
      if (!columnKey) {
        return false
      }

      const normalizedKey = String(columnKey).toLowerCase()
      const keyWithoutPrefix = normalizedKey.startsWith('data.')
        ? normalizedKey.slice(5)
        : normalizedKey

      const alwaysExcludedColumns = {
        bucketkey: true,
        bucketlabel: true,
        categoryid: true,
        'data.beaconfee': true
      }

      if (alwaysExcludedColumns[normalizedKey]) {
        return true
      }

      if (normalizedKey === 'data.fees' || normalizedKey.startsWith('data.fees.')) {
        return true
      }

      if (keyWithoutPrefix === 'totalrefundamount') {
        return true
      }

      if (keyWithoutPrefix === 'feedeductions.feededuction') {
        return true
      }

      if (keyWithoutPrefix === 'paidoutamount' || keyWithoutPrefix === 'pendingpayoutamount') {
        return true
      }

      if (!this.showWeekendRevenue && keyWithoutPrefix.includes('weekend')) {
        return true
      }

      return false
    },
    getCsvHeaderLabel (columnKey) {
      if (!columnKey) {
        return ''
      }

      const key = String(columnKey)
      const customCsvHeaderLabels = {
        'data.paidOut': 'Paid out',
        'data.paidOutDate': 'Payout date'
      }

      if (customCsvHeaderLabels[key]) {
        return customCsvHeaderLabels[key]
      }

      if (key.startsWith('data.')) {
        return key.slice(5)
      }

      return key
    },
    getChartCsvColumns (rows) {
      const baseColumns = [
        'frequency',
        'periodStart',
        'vehicleName'
      ]
      const extraColumnsMap = {}
      const safeRows = Array.isArray(rows) ? rows : []

      for (let i = 0; i < safeRows.length; i += 1) {
        const row = safeRows[i] || {}
        const rowKeys = Object.keys(row)
        for (let j = 0; j < rowKeys.length; j += 1) {
          const key = rowKeys[j]
          if (this.shouldExcludeCsvColumn(key)) {
            continue
          }
          if (baseColumns.includes(key)) {
            continue
          }
          extraColumnsMap[key] = true
        }
      }

      const prioritizedDataKeys = [
        'data.ticketCount',
        'data.grossAmount',
        'data.totalAmount',
        'data.totalTicketAmountBeforeFees'
      ]
      const dynamicColumns = Object.keys(extraColumnsMap).sort()
      const prioritizedColumns = []
      const remainingColumns = []

      for (let i = 0; i < dynamicColumns.length; i += 1) {
        const key = dynamicColumns[i]
        if (prioritizedDataKeys.includes(key)) {
          prioritizedColumns.push(key)
        } else {
          remainingColumns.push(key)
        }
      }

      const orderedPrioritizedColumns = prioritizedDataKeys.filter((key) => prioritizedColumns.includes(key))
      const orderedColumns = baseColumns.concat(orderedPrioritizedColumns).concat(remainingColumns)

      const adHocNotesIndex = orderedColumns.indexOf('data.adHocNotes')
      const totalFeeDeductionIndex = orderedColumns.indexOf('data.totalFeeDeduction')

      if (adHocNotesIndex > -1 && totalFeeDeductionIndex > -1 && totalFeeDeductionIndex !== adHocNotesIndex + 1) {
        orderedColumns.splice(totalFeeDeductionIndex, 1)
        orderedColumns.splice(adHocNotesIndex + 1, 0, 'data.totalFeeDeduction')
      }

      const payoutColumns = ['data.paidOut', 'data.paidOutDate']
      for (let i = 0; i < payoutColumns.length; i += 1) {
        const payoutColumn = payoutColumns[i]
        const payoutColumnIndex = orderedColumns.indexOf(payoutColumn)

        if (payoutColumnIndex > -1) {
          orderedColumns.splice(payoutColumnIndex, 1)
        }

        if (extraColumnsMap[payoutColumn]) {
          orderedColumns.push(payoutColumn)
        }
      }

      return orderedColumns
    },
    csvSafeTitle (title) {
      return String(title || 'report')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },
    buildChartCsvFileName (chartState, frequency) {
      const reportWindowStart = this.dateFrom || moment.utc().format('YYYY-MM-DD')
      const reportWindowEnd = this.dateTo || reportWindowStart
      const chartName = this.csvSafeTitle(chartState && chartState.title ? chartState.title : (frequency + '-report'))
      return chartName + '-' + reportWindowStart + '-to-' + reportWindowEnd + '.csv'
    },
    async downloadChartCsv (chartState, frequency) {
      const rows = chartState && Array.isArray(chartState.reportRows) ? chartState.reportRows : []
      if (!rows.length) {
        throw new Error('No chart data available to download.')
      }

      const columns = this.getChartCsvColumns(rows)

      const sortedRows = rows.slice().sort(function (a, b) {
        const aTime = a && a.periodStart ? new Date(a.periodStart).getTime() : 0
        const bTime = b && b.periodStart ? new Date(b.periodStart).getTime() : 0
        return bTime - aTime
      })
      return this.rowsToCsv(columns, sortedRows)
    },
    downloadDayChartCsv () {
      return this.downloadChartCsv(this.dayChart, 'day')
    },
    downloadWeekChartCsv () {
      return this.downloadChartCsv(this.weekChart, 'week')
    },
    downloadMonthChartCsv () {
      return this.downloadChartCsv(this.monthChart, 'month')
    },
    downloadQuarterChartCsv () {
      return this.downloadChartCsv(this.quarterChart, 'quarter')
    },
    downloadYearChartCsv () {
      return this.downloadChartCsv(this.yearChart, 'year')
    },
    async buildChartForFrequency (frequency, vehicleIds) {
      const chartTitleByFrequency = {
        day: 'Revenue per Day',
        week: 'Revenue per Week',
        month: 'Revenue per Month',
        quarter: 'Revenue per Quarter',
        year: 'Revenue per Year'
      }

      const { rangeStart, rangeEndExclusive } = this.resolveRangeForFrequency(frequency)
      const apiPeriod = this.resolveApiPeriodBounds()
      const scopedVehicleIds = Array.isArray(vehicleIds) ? vehicleIds : []
      if (!scopedVehicleIds.length) {
        return this.emptyChartState(chartTitleByFrequency[frequency])
      }

      const where = {
        reportType: 'ticket_revenue',
        reportStatus: 'completed',
        category: 'transportation',
        categoryId: { inq: scopedVehicleIds },
        periodStart: { gte: apiPeriod.periodStart },
        periodEnd: { lte: apiPeriod.periodEnd }
      }

      let records = []
      if (frequency === 'day') {
        records = await findSourceRecords({
          where: where,
          fields: { categoryId: true, periodStart: true, data: true },
          limit: 10000
        })
      } else {
        where.reportFidelity = frequency
        records = await findAggregateRecords({
          where: where,
          fields: { categoryId: true, periodStart: true, data: true },
          limit: 10000
        })
      }

      const buckets = this.buildBuckets(frequency, rangeStart, rangeEndExclusive)
      const bucketByKey = {}
      for (let i = 0; i < buckets.length; i += 1) {
        const key = buckets[i].key
        bucketByKey[key] = this.emptyBucket()
      }

      for (let i = 0; i < records.length; i += 1) {
        const record = records[i]
        const bucketKey = this.bucketKeyForDate(frequency, record.periodStart)
        if (!bucketByKey[bucketKey]) {
          continue
        }

        const bucket = bucketByKey[bucketKey]
        const vehicleId = record && record.categoryId ? String(record.categoryId) : null
        const data = record && record.data ? record.data : {}
        const feeDeductions = data && data.feeDeductions ? data.feeDeductions : {}

        bucket.totalAmount += toNumber(data.totalAmount)
        bucket.totalFeeDeduction += toNumber(data.totalFeeDeduction)
        bucket.totalTicketAmountBeforeFees += toNumber(data.totalTicketAmountBeforeFees)
        bucket.adHocAmountBeforeFees += toNumber(data.adHocAmountBeforeFees)
        bucket.smarttransytFee += toNumber(feeDeductions.smarttransytFee)
        bucket.perDayDriverFee += toNumber(feeDeductions.perDayDriverFee)
        bucket.totalKwhCost += toNumber(feeDeductions.totalKwhCost || feeDeductions.perKwHFee)
        bucket.totalRefundAmount += toNumber(data.totalRefundAmount)
        bucket.weekendAmount += toNumber(data.weekendAmount)
        bucket.weekendRefundAmount += toNumber(data.weekendRefundAmount)

        if (data.paidOut === true) {
          bucket.paidOut = true
        }

        const paidOutDateValues = Array.isArray(data.paidOutDate)
          ? data.paidOutDate
          : (data.paidOutDate
            ? [data.paidOutDate]
            : []
          )

        for (let d = 0; d < paidOutDateValues.length; d += 1) {
          const paidOutDateValue = String(paidOutDateValues[d] || '').trim()

          if (!paidOutDateValue || bucket.paidOutDates.includes(paidOutDateValue)) {
            continue
          }

          bucket.paidOutDates.push(paidOutDateValue)
        }

        if (vehicleId) {
          this.addBucketMetricByVehicle(bucket, 'totalAmountByVehicle', vehicleId, data.totalAmount)
          this.addBucketMetricByVehicle(bucket, 'totalFeeDeductionByVehicle', vehicleId, data.totalFeeDeduction)
          this.addBucketMetricByVehicle(bucket, 'totalTicketAmountBeforeFeesByVehicle', vehicleId, data.totalTicketAmountBeforeFees)
          this.addBucketMetricByVehicle(bucket, 'adHocAmountBeforeFeesByVehicle', vehicleId, data.adHocAmountBeforeFees)
          this.addBucketMetricByVehicle(bucket, 'smarttransytFeeByVehicle', vehicleId, feeDeductions.smarttransytFee)
          this.addBucketMetricByVehicle(bucket, 'perDayDriverFeeByVehicle', vehicleId, feeDeductions.perDayDriverFee)
          this.addBucketMetricByVehicle(bucket, 'totalKwhCostByVehicle', vehicleId, feeDeductions.totalKwhCost || feeDeductions.perKwHFee)
          this.addBucketMetricByVehicle(bucket, 'totalRefundAmountByVehicle', vehicleId, data.totalRefundAmount)
          this.addBucketMetricByVehicle(bucket, 'weekendAmountByVehicle', vehicleId, data.weekendAmount)
          this.addBucketMetricByVehicle(bucket, 'weekendRefundAmountByVehicle', vehicleId, data.weekendRefundAmount)
          this.addBucketMetricByVehicle(bucket, 'ticketCountByVehicle', vehicleId, data.ticketCount)
          this.addBucketMetricByVehicle(bucket, 'weekendTicketCountByVehicle', vehicleId, data.weekendTicketCount)

          if (frequency === 'day' && Array.isArray(data.adHocNotes) && data.adHocNotes.length) {
            if (!bucket.adHocNotesByVehicle[vehicleId]) {
              bucket.adHocNotesByVehicle[vehicleId] = []
            }

            for (let n = 0; n < data.adHocNotes.length; n += 1) {
              const noteEntry = String(data.adHocNotes[n] || '').trim()
              if (noteEntry) {
                bucket.adHocNotesByVehicle[vehicleId].push(noteEntry)
              }
            }
          }
        }
      }

      return {
        title: chartTitleByFrequency[frequency],
        categories: buckets.map(function (item) { return item.label }),
        buckets: buckets.map(function (item) { return bucketByKey[item.key] || {} }),
        reportRows: this.buildReportRows(records, frequency)
      }
    },
    buildBuckets (frequency, rangeStart, rangeEndExclusive) {
      const buckets = []

      if (frequency === 'day') {
        let cursor = rangeStart.clone()
        while (cursor.isBefore(rangeEndExclusive)) {
          buckets.push({
            key: cursor.format('YYYY-MM-DD'),
            label: cursor.format('ddd YYYY-MM-DD')
          })
          cursor = cursor.add(1, 'day')
        }
        return buckets
      }

      const unitByFrequency = {
        week: 'week',
        month: 'month',
        quarter: 'quarter',
        year: 'year'
      }
      const startMethodByFrequency = {
        week: 'isoWeek',
        month: 'month',
        quarter: 'quarter',
        year: 'year'
      }

      const unit = unitByFrequency[frequency]
      const startMethod = startMethodByFrequency[frequency]
      let cursor = rangeStart.clone().startOf(startMethod)

      while (cursor.isBefore(rangeEndExclusive)) {
        const bucketStart = cursor.clone()
        const bucketEnd = cursor.clone().add(1, unit)

        if (bucketStart.isSameOrAfter(rangeStart) && bucketEnd.isSameOrBefore(rangeEndExclusive)) {
          buckets.push({
            key: this.bucketKeyForDate(frequency, bucketStart.toISOString()),
            label: this.bucketLabelForDate(frequency, bucketStart.toISOString())
          })
        }

        cursor = cursor.add(1, unit)
      }

      return buckets
    },
    bucketKeyForDate (frequency, dateIso) {
      const date = moment.utc(dateIso)
      if (frequency === 'day') {
        return date.format('YYYY-MM-DD')
      }
      if (frequency === 'week') {
        return date.startOf('isoWeek').format('YYYY-MM-DD')
      }
      if (frequency === 'month') {
        return date.startOf('month').format('YYYY-MM')
      }
      if (frequency === 'quarter') {
        return date.startOf('quarter').format('YYYY-[Q]Q')
      }
      return date.startOf('year').format('YYYY')
    },
    bucketLabelForDate (frequency, dateIso) {
      const date = moment.utc(dateIso)
      if (frequency === 'week') {
        const weekStart = date.clone().startOf('isoWeek')
        const weekEnd = weekStart.clone().add(6, 'day')
        return weekStart.format('YYYY-MM-DD') + ' to ' + weekEnd.format('YYYY-MM-DD')
      }
      if (frequency === 'month') {
        return date.format('MMM YYYY')
      }
      if (frequency === 'quarter') {
        return 'Q' + date.format('Q YYYY')
      }
      return date.format('YYYY')
    },
    sumMetricValues (metricByVehicle) {
      const source = metricByVehicle && typeof metricByVehicle === 'object' ? metricByVehicle : {}
      return Object.keys(source).reduce(function (sum, key) {
        return sum + toNumber(source[key])
      }, 0)
    },
    buildMoneySeries (buckets, seriesConfigs) {
      const configs = Array.isArray(seriesConfigs) ? seriesConfigs : []
      const safeBuckets = Array.isArray(buckets) ? buckets : []

      return configs.map(function (config) {
        return {
          name: config.name,
          data: safeBuckets.map(function (bucket) {
            return toRoundedMoney(bucket && config.totalKey ? bucket[config.totalKey] : 0)
          })
        }
      })
    },
    buildTicketCountSeries (buckets, useWeekend) {
      const safeBuckets = Array.isArray(buckets) ? buckets : []
      const sourceKey = useWeekend ? 'weekendTicketCountByVehicle' : 'ticketCountByVehicle'
      const self = this

      const combinedName = useWeekend ? 'Weekend Tickets' : 'Tickets'
      return [{
        name: combinedName,
        data: safeBuckets.map(function (bucket) {
          return toRoundedCount(self.sumMetricValues(bucket ? bucket[sourceKey] : {}))
        })
      }]
    },
    buildSeriesForView (chartState) {
      const buckets = chartState && Array.isArray(chartState.buckets) ? chartState.buckets : []
      if (!buckets.length) {
        return []
      }

      if (this.showWeekendRevenue) {
        if (this.selectedChartView === 'tickets') {
          return this.buildTicketCountSeries(buckets, true)
        }
        if (this.selectedChartView === 'refunds' && !this.isTransportationOwnerUser) {
          return this.buildMoneySeries(buckets, [{
            name: 'Weekend Refunds',
            totalKey: 'weekendRefundAmount',
            byVehicleKey: 'weekendRefundAmountByVehicle'
          }])
        }
        return this.buildMoneySeries(buckets, [{
          name: 'Weekend Revenue',
          totalKey: 'weekendAmount',
          byVehicleKey: 'weekendAmountByVehicle'
        }])
      }

      if (this.selectedChartView === 'sources') {
        return this.buildMoneySeries(buckets, [
          {
            name: 'Ticket Revenue',
            totalKey: 'totalTicketAmountBeforeFees',
            byVehicleKey: 'totalTicketAmountBeforeFeesByVehicle'
          },
          {
            name: 'Ad-hoc Revenue',
            totalKey: 'adHocAmountBeforeFees',
            byVehicleKey: 'adHocAmountBeforeFeesByVehicle'
          }
        ])
      }

      if (this.selectedChartView === 'fees') {
        return this.buildMoneySeries(buckets, [
          {
            name: 'SmartTransit Fee',
            totalKey: 'smarttransytFee',
            byVehicleKey: 'smarttransytFeeByVehicle'
          },
          {
            name: 'Driver Fee',
            totalKey: 'perDayDriverFee',
            byVehicleKey: 'perDayDriverFeeByVehicle'
          },
          {
            name: 'Charging cost',
            totalKey: 'totalKwhCost',
            byVehicleKey: 'totalKwhCostByVehicle'
          }
        ])
      }

      if (this.selectedChartView === 'tickets') {
        return this.buildTicketCountSeries(buckets, false)
      }

      if (this.selectedChartView === 'refunds' && !this.isTransportationOwnerUser) {
        return this.buildMoneySeries(buckets, [{
          name: 'Refunds',
          totalKey: 'totalRefundAmount',
          byVehicleKey: 'totalRefundAmountByVehicle'
        }])
      }

      return this.buildMoneySeries(buckets, [
        {
          name: 'Net Revenue',
          totalKey: 'totalAmount',
          byVehicleKey: 'totalAmountByVehicle'
        },
        {
          name: 'Total Fees',
          totalKey: 'totalFeeDeduction',
          byVehicleKey: 'totalFeeDeductionByVehicle'
        }
      ])
    },
    chartOptions (chartState) {
      const isTicketView = this.selectedChartView === 'tickets'
      const self = this
      return {
        chart: {
          type: 'bar',
          stacked: !isTicketView,
          toolbar: { show: false }
        },
        dataLabels: { enabled: false },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%'
          }
        },
        xaxis: {
          categories: chartState.categories
        },
        yaxis: {
          title: {
            text: isTicketView ? 'Ticket Count' : ('Amount' + (self.reportCurrencySymbol ? ' ' + self.reportCurrencySymbol : ''))
          },
          labels: {
            formatter: function (value) {
              if (isTicketView) {
                return String(toRoundedCount(value))
              }

              return self.formatAmountAxisLabel(value)
            }
          }
        },
        tooltip: {
          y: {
            formatter: function (value) {
              if (value === null || value === undefined) { return '' }
              if (isTicketView) {
                return String(toRoundedCount(value))
              }

              return self.formatAmountLabel(value)
            }
          }
        }
      }
    },
    renderChartState (chartState) {
      const state = chartState || this.emptyChartState('Revenue')
      const series = this.buildSeriesForView(state)
      const hasData = series.some(function (item) {
        const data = Array.isArray(item.data) ? item.data : []
        return data.some(function (value) { return toNumber(value) > 0 })
      })

      return {
        title: state.title,
        categories: state.categories,
        series: series,
        hasData: hasData
      }
    }
  }
}
